<template>
  <div class="spa-checkout-form">
    <div class="spa-checkout-form__wrapper">
      <spa-checkout-form-handler
        ref="spaCheckoutFormHandler"
        :key="JSON.stringify(authUser)"
        :redirect-url="spaReservationDetailsPage"
        @loading="isLoading = $event"
        @error="onExternalError($event)"
        @external-error="onExternalError($event)"
      >
        <form
          slot-scope="{ serverErrorMsg }"
          class="mohg-form spa-checkout-form__form"
        >
          <SpaGuestDetailsForm
            v-for="(guest, index) in guestsSelectedData"
            :key="`guest-${index}`"
            :index="index"
            @guest-validation-changed="updateGuestValidationStatus"
          />
          <transition-group
            v-show="paymentSectionIsVisible"
            name="slide-fade"
            tag="div"
          >
            <SpaPaymentDetailsForm
              key="spaPaymentDetailsForm"
              :all-guests-valid="allGuestsValid"
              @payment-validation-changed="updatePaymentValidationStatus"
              @terms-and-conditions-validation-changed="updateTermsAndConditionsValidationStatus"
            />
          </transition-group>
          <button
            :type="paymentSectionIsVisible ? 'submit' : 'button'"
            class="button--primary black size-lg primary"
            :class="[{ loading: isLoading }]"
            :disabled="formCtaIsDisabled"
            :aria-disabled="formCtaIsDisabled"
            @click.prevent.prevent="handleFormCtaClick"
            @keydown.enter.prevent="handleFormCtaClick"
            @keydown.space.prevent="handleFormCtaClick"
          >
            <div
              class="sr-only"
              role="status"
            >
              {{ isLoading ? tc('accessibility.loadingButtonStatus') : '' }}
            </div>
            {{ formCtaLabel }}
          </button>
          <div
            v-if="serverErrorMsg"
            class="error-box"
            role="status"
          >
            <span>{{ serverErrorMsg }}</span>
          </div>
        </form>
      </spa-checkout-form-handler>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import Tracking from '@mixins/tracking'
  import SpaCheckoutFormHandler from './spa-checkout-form-handler'
  import SpaGuestDetailsForm from './spa-guest-details-form.vue'
  import SpaPaymentDetailsForm from './spa-payment-details-form.vue'

  export default {
    name: 'SpaCheckOutForm',
    components: {
      SpaCheckoutFormHandler,
      SpaGuestDetailsForm,
      SpaPaymentDetailsForm,
    },
    mixins: [Tracking],
    inject: ['spaReservationDetailsPage'],
    data() {
      return {
        guestValidationStates: {},
        paymentDataIsValid: false,
        termsAndConditionsValid: false,
        paymentSectionIsVisible: false,
        isLoading: false,
      }
    },
    computed: {
      ...mapState('auth', { authUser: 'user' }),
      ...mapState('spaSoft', ['propertyData', 'selectedDate', 'numberOfGuests', 'selectedItinerary']),
      ...mapGetters('spaSoft', ['guestsSelectedData', 'getTotalCost', 'getTotalTaxes', 'getStartTime', 'getEndTime']),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('currency', ['getDisplayPrice', 'orSelected']),
      allGuestsValid() {
        if (!Object.keys(this.guestValidationStates).length) return false

        return Object.values(this.guestValidationStates).every((status) => status === true)
      },
      formCtaIsDisabled() {
        if (this.paymentSectionIsVisible) {
          return !this.allGuestsValid || !this.paymentDataIsValid || !this.termsAndConditionsValid
        }
        return !this.allGuestsValid
      },
      formCtaLabel() {
        return this.paymentSectionIsVisible ? this.tc('spaSoft.step2.completeBookingCta') : this.tc('spaSoft.step2.paymentInformation.continueToPayment')
      }
    },
    watch: {
      paymentSectionIsVisible(val) {
        if (val) {
          this.trackSpaCheckoutInit()
        }
      }
    },
    mounted() {
      this.$nextTick(() => {
        if (Object.keys(this.guestsSelectedData).length === 1) {
          this.$refs.spaCheckoutFormHandler?.updateDefaultValues()
          if (this.authUser?.phoneNumbers?.length) {
            this.$refs.spaCheckoutFormHandler?.onExternalValidation({ data: { validationType: 'phone' } })
          }
          if (this.authUser?.email) {
            this.$refs.spaCheckoutFormHandler?.onExternalValidation({ data: { validationType: 'email' } })
          }
        }
      })
    },
    methods: {
      updateGuestValidationStatus({ index, status }) {
        this.$set(this.guestValidationStates, index, status)
      },
      updatePaymentValidationStatus(paymentDataStatus) {
        this.paymentDataIsValid = paymentDataStatus
      },
      updateTermsAndConditionsValidationStatus(termsAndConditionsStatus) {
        this.termsAndConditionsValid = termsAndConditionsStatus
      },
      onExternalError() {
        this.isLoading = false
      },
      handleFormCtaClick() {
        if (!this.paymentSectionIsVisible) {
          this.paymentSectionIsVisible = true
        } else {
          this.$refs.spaCheckoutFormHandler.form.submit()
        }
      },
      trackSpaCheckoutInit() {
        this.$track(this.getTrackingData('spaSoft', null, {
          event: 'spaCheckoutInit',
          hotelCode: this.hotelCode,
          stepNumber: 3.1,
          checkout: {
            numberOfGuests: this.numberOfGuests,
            date: this.selectedDate,
            cartTotalPrice: this.getTotalCost({
              isReservation: false, formatted: true, noSymbol: true, withIsoCode: false
            }),
            currency: this.orSelected(this.propertyData?.currency?.isoCode),
            taxesAndFees: this.getTotalTaxes({
              isReservation: false, formatted: true, noSymbol: true, withIsoCode: false
            }),
            guestInfo: this.selectedItinerary.guestAvailabilities.map((guest, index) => ({
              guestNumber: index + 1,
              guestType: guest.guest.guestType,
              guestGender: guest.guest.gender,
              phoneType: guest.guest.phoneType
            })),
            products: Object.values(this.guestsSelectedData).flatMap((guest, guestIndex) => guest.selectedTreatments.map((treatment) => ({
              guestNumber: guestIndex + 1,
              productType: 'Treatment',
              treatmentType: treatment.treatmentCategory.title,
              treatmentName: treatment.sitecoreTreatmentDetails.title,
              duration: treatment.selectedTimeSlot.duration,
              price: this.getDisplayPrice({
                price: treatment.selectedTimeSlot.apiTreatmentDetails.price.totalCalculated,
                fromCurrency: this.propertyData?.currency?.isoCode,
                toCurrency: this.orSelected(this.propertyData?.currency?.isoCode),
                noSymbol: true
              }),
              currency: this.orSelected(this.propertyData?.currency?.isoCode),
              appointmentTime: `${this.getStartTime(treatment, guestIndex)} - ${this.getEndTime(treatment, guestIndex)}`,
              preferredTherapistGender: treatment.selectedTimeSlot.preferredGender
            })))
          }
        }))
      }
    }
  }
</script>
